import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import type { Easing } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Play } from 'lucide-react';

const ease: Easing = [0.25, 0.1, 0.25, 1];

const line = (delay: number) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1.2, delay, ease },
  style: { willChange: 'transform, opacity' as const },
});

/** Check if connection is slow (2G/3G) */
const isSlowConnection = () => {
  const conn = (navigator as any).connection;
  if (!conn) return false;
  return conn.saveData || ['slow-2g', '2g', '3g'].includes(conn.effectiveType);
};

const HeroSection = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(false);
  const [videoActivated, setVideoActivated] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // Deferred video activation: only after window.load AND if not slow connection
  useEffect(() => {
    if (isSlowConnection() && isMobile) {
      setVideoFailed(true);
      return;
    }

    const activate = () => setVideoActivated(true);

    if (document.readyState === 'complete') {
      activate();
    } else {
      window.addEventListener('load', activate, { once: true });
      return () => window.removeEventListener('load', activate);
    }
  }, [isMobile]);

  // Once activated, set the real src from data-src
  useEffect(() => {
    if (!videoActivated) return;
    const video = videoRef.current;
    if (!video) return;

    const sources = video.querySelectorAll('source[data-src]');
    sources.forEach((source) => {
      const dataSrc = source.getAttribute('data-src');
      if (dataSrc) {
        source.setAttribute('src', dataSrc);
        source.removeAttribute('data-src');
      }
    });
    video.load();
  }, [videoActivated]);

  // iOS-safe play attempt
  const tryPlay = useCallback(() => {
    const video = videoRef.current;
    if (!video || videoFailed) return;
    if (document.visibilityState !== 'visible') return;
    if (video.readyState < 2) return;

    video.muted = true;
    video.playsInline = true;
    video.play().then(() => {
      setShowPlayButton(false);
    }).catch(() => {});
  }, [videoFailed]);

  const resetVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video || videoFailed) return;
    if (document.visibilityState !== 'visible') return;

    video.muted = true;
    video.playsInline = true;

    if (video.paused && video.readyState < 3) {
      video.pause();
      video.currentTime = 0;
      video.load();

      window.setTimeout(() => {
        video.play().then(() => {
          setShowPlayButton(false);
        }).catch(() => {
          window.setTimeout(() => {
            videoRef.current?.play().then(() => {
              setShowPlayButton(false);
            }).catch(() => {});
          }, 800);
        });
      }, 300);

      return;
    }

    video.play().then(() => {
      setShowPlayButton(false);
    }).catch(() => {});
  }, [videoFailed]);

  // Reset state on mount
  useEffect(() => {
    setVideoReady(false);
    setVideoFailed(false);
    setShowPlayButton(false);
  }, []);

  // SPA navigation: reset video when returning to Home
  useEffect(() => {
    if (location.pathname === '/') {
      const timer = setTimeout(() => resetVideo(), 300);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, resetVideo]);

  // Browser events: bfcache, tab switch, focus, pagehide, freeze/resume, network
  useEffect(() => {
    if (!videoActivated) return;
    resetVideo();

    const onInteraction = () => tryPlay();
    window.addEventListener('touchstart', onInteraction, { once: true, passive: true });
    window.addEventListener('click', onInteraction, { once: true });

    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) resetVideo();
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') resetVideo();
    };
    const handleFocus = () => tryPlay();
    const handlePageHide = () => {
      videoRef.current?.pause();
    };
    const handleFreeze = () => {
      videoRef.current?.pause();
    };
    const handleResume = () => tryPlay();
    const handleNetworkChange = () => resetVideo();

    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    document.addEventListener('freeze', handleFreeze);
    document.addEventListener('resume', handleResume);
    (navigator as Navigator & { connection?: EventTarget }).connection?.addEventListener('change', handleNetworkChange);

    return () => {
      window.removeEventListener('touchstart', onInteraction);
      window.removeEventListener('click', onInteraction);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
      document.removeEventListener('freeze', handleFreeze);
      document.removeEventListener('resume', handleResume);
      (navigator as Navigator & { connection?: EventTarget }).connection?.removeEventListener('change', handleNetworkChange);
    };
  }, [isMobile, videoActivated, resetVideo, tryPlay]);

  // IntersectionObserver — pause when off-screen, play when visible
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoActivated) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          tryPlay();
        } else {
          video.pause();
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [videoFailed, isMobile, videoActivated, tryPlay]);

  // Low Power Mode fallback — only show play button after generous delay
  useEffect(() => {
    if (!videoActivated) return;
    const timer = setTimeout(() => {
      const video = videoRef.current;
      if (video && video.paused && video.readyState >= 2 && !videoFailed) {
        setShowPlayButton(true);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [videoFailed, videoActivated]);

  // Failsafe: retry for 4s in case no lifecycle listener fired
  useEffect(() => {
    if (location.pathname !== '/' || !videoActivated) return;

    let attempts = 0;
    const interval = window.setInterval(() => {
      const video = videoRef.current;
      if (!video) {
        window.clearInterval(interval);
        return;
      }

      if (video.paused && document.visibilityState === 'visible') {
        attempts += 1;
        resetVideo();
      }

      if (attempts >= 5 || !video.paused) {
        window.clearInterval(interval);
      }
    }, 800);

    return () => window.clearInterval(interval);
  }, [location.pathname, videoActivated, resetVideo]);

  const handleManualPlay = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    video.play().then(() => setShowPlayButton(false)).catch(() => {});
  };

  return (
    <section
      className="hero relative w-full overflow-hidden"
      style={{ height: 'calc(var(--vh, 1vh) * 100)' }}
    >
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,_hsl(0_0%_10%)_0%,_hsl(0_0%_5.5%)_70%)]" />

      <div className="absolute inset-0 overflow-hidden">
        {/* Poster image — always visible as base layer, eager loaded for LCP */}
        <img
          src="/video/hero-poster.jpg"
          alt=""
          aria-hidden="true"
          width={1920}
          height={1080}
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ aspectRatio: '16/9' }}
          fetchPriority="high"
        />

        {/* Video layer — all devices, deferred loading via data-src */}
        {!videoFailed && videoActivated && (
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            // @ts-ignore — legacy iOS attribute
            webkit-playsinline=""
            preload="none"
            poster="/video/hero-poster.jpg"
            onCanPlay={() => { setVideoReady(true); tryPlay(); }}
            onError={() => setVideoFailed(true)}
            onStalled={() => {
              setTimeout(() => {
                if (videoRef.current && videoRef.current.readyState < 3) {
                  setVideoFailed(true);
                }
              }, 8000);
            }}
            className={`absolute top-0 left-0 w-full h-full object-cover object-center transition-opacity duration-1000 ${videoReady ? 'opacity-100' : 'opacity-0'}`}
            style={{ backgroundColor: '#0a0a0a', aspectRatio: '16/9' }}
          >
            <source
              data-src="/video/video-desktop-hq.mp4"
              type="video/mp4"
            />
          </video>
        )}

        {/* Manual play button — Low Power Mode / autoplay blocked */}
        {showPlayButton && !videoFailed && (
          <button
            onClick={handleManualPlay}
            aria-label={t('brooklyn.hero.playVideo', 'Play background video')}
            className="absolute bottom-6 right-6 z-[2] w-12 h-12 min-w-[48px] min-h-[48px] rounded-full bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center hover:bg-white/25 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground/50"
          >
            <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
          </button>
        )}

        <div
          className="hero-overlay absolute inset-0 z-[1]"
          style={{ background: 'rgba(0, 0, 0, 0.45)' }}
        />
      </div>

      <div className="hero-content absolute inset-0 z-[2] flex flex-col items-center justify-center px-6 pt-[100px] pb-12 md:pt-[120px] md:pb-16 text-center">
        <motion.h1 {...line(0)} className="text-display text-foreground leading-[1.1] mb-4 max-w-[900px] mx-auto tracking-[-0.02em] text-[clamp(2rem,7vw,3.5rem)] md:text-[clamp(2.8rem,5.5vw,5rem)] xl:text-[clamp(3.5rem,4.5vw,5.5rem)]">
          {t('brooklyn.hero.headline')}
        </motion.h1>

        <motion.p {...line(0.3)} className="text-ui text-[clamp(0.85rem,1.8vw,1.05rem)] text-foreground/75 tracking-[0.06em] max-w-[520px] mx-auto mb-8 leading-relaxed" style={{ textShadow: '0 1px 12px rgba(0,0,0,0.6)' }}>
          {t('brooklyn.hero.sub')}
        </motion.p>

        <motion.p {...line(0.6)} className="text-ui text-[10px] sm:text-xs text-muted-foreground tracking-[0.25em] uppercase mb-10">
          {t('brooklyn.hero.signature')}
        </motion.p>

        <motion.div {...line(0.9)} className="mt-4">
          <Link to="/apply" className="btn-outline">
            {t('brooklyn.hero.cta')} →
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
